import React, { useContext, useEffect, useState } from 'react';

import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import { DeleteOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CardMedia,
  Chip,
  Divider,
  Grid,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import {
  gray100,
  gray70,
  gray90,
  green10,
  green100,
  primary10,
  primaryColor,
  tertiaryButtonOutlinedColor
} from '../../../constants/Colors';
import { operatorFlg, userType } from '../../../constants/Enum';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import { productMethodList } from '../../../constants/SelectOptions';
import { AuthContext } from '../../../contexts/auth';

import noImage from '../../assets/noImage.svg';
import CustomDeleteDialog from '../../uiParts/CustomDeleteDialog';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import SideBar from '../../uiParts/SideBar';
import ReviewInputBox from '../../uiParts/ReviewInputBox';
import CustomHead from '../CustomHead';
import { getAdminProductEvaluation, getProduct } from '../../../api/db/product';
import { AdminEvaluationProductType } from '../../../validation/products/productType';
import { EvaluationFormType } from '../../../validation/evaluations/evaluationsType';
import { AxiosError } from 'axios';
import { response } from '../../../api/db';
import { common } from '../../../modules/error';
import { adminDeleteReview } from '../../../api/db/evaluation';
import { sentryLog } from '../../../config/sentry';

const styles = {
  alias: {
    '@media screen and (max-width: 600px)': {
      fontSize: '12px'
    },
    color: gray100,
    fontSize: '14px'
  },
  buttonWidth: {
    '@media screen and (max-width: 600px)': {
      width: '100%'
    },
    width: '200px'
  },
  category: {
    '@media screen and (max-width: 600px)': {
      fontSize: '12px'
    },
    color: gray90,
    fontSize: '14px'
  },
  co2Value: {
    color: primaryColor,
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: '26px',
    mr: '4px',
    verticalAlign: 'bottom',
    '@media screen and (max-width: 600px)': {
      fontSize: '24px'
    }
  },
  co2ValueIcon: {
    color: primaryColor,
    height: '20px',
    width: '20px',
    verticalAlign: 'bottom'
  },
  co2ValueItem: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '14px',
    mr: '4px',
    verticalAlign: 'bottom'
  },
  co2ValueUnit: {
    color: gray100,
    fontSize: '14px',
    lineHeight: '14px',
    verticalAlign: 'bottom'
  },
  contentWrapper: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column'
    },
    flexDirection: 'row',
    mt: '24px'
  },
  contentDetail: {
    '@media screen and (max-width:960px)': {
      width: '100%',
      pl: 0
    },
    width: '70%',
    pl: '24px'
  },
  deleteButton: {
    boxSizing: 'border-box',
    height: '44px',
    m: '0 0 0 auto',
    '@media screen and (max-width: 600px)': {
      width: '100%'
    }
  },
  headerTitle: {
    '@media screen and (max-width: 600px)': {
      fontSize: '24px'
    },
    fontSize: '32px',
    fontWeight: 500,
    color: gray100
  },
  imageForSp: {
    '@media screen and (max-width:960px)': {
      display: 'block',
      maxWidth: '248px',
      minWidth: '128px'
    },
    display: 'none'
  },
  imageWrapper: {
    '@media screen and (max-width:960px)': {
      display: 'none'
    },
    width: '30%'
  },
  name: {
    '@media screen and (max-width: 600px)': {
      fontSize: '16px'
    },
    color: gray100,
    fontSize: '24px',
    fontWeight: 700
  },
  subTitle: {
    '@media screen and (max-width: 600px)': {
      fontSize: '14px'
    },
    color: gray100,
    fontWeight: 500,
    verticalAlign: 'middle'
  },
  textOverflow: {
    '@media screen and (max-width: 600px)': {
      width: '100px'
    },
    display: 'inline-block',
    ml: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  convValueChip: {
    bgcolor: primary10,
    color: primaryColor,
    fontSize: '12px',
    fontWeight: '500',
    height: '21px',
    mr: '8px'
  },
  calcValueChip: {
    bgcolor: green10,
    color: green100,
    fontSize: '12px',
    fontWeight: '500',
    height: '21px',
    mr: '8px'
  }
};

const AdminEvaluation: React.FC = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const { id } = useParams();
  const currentUserType = Number(localStorage.getItem('user_type'));
  const navigate = useNavigate();

  const [selectedSort, setSelectedSort] = useState(1);
  const handleChangeSort = (event: SelectChangeEvent<unknown>) => {
    setSelectedSort(Number(event.target.value));
  };

  useEffect(() => {
    // 未ログイン時はログイン画面に飛ばす
    if (!isLoggedIn) return navigate('/auth/login');
    // 事務局画面で事務局ユーザーではない場合は、ホームに飛ばす
    if (currentUserType !== userType.admin) return navigate('/home');
  }, [isLoggedIn, currentUserType]);

  const [co2Unit, setCo2Unit] = useState('kg-CO2/kg');
  const [productData, setProductData] = useState<AdminEvaluationProductType>({
    id: 0,
    alias: '',
    category: '',
    name: '',
    specification: '',
    calculatedEmissionIntensity: 0,
    calculatedMethod: null,
    calculatedReduceRate: 0,
    image: [
      {
        id: 0,
        name: '',
        path: '',
        sort: 0
      }
    ]
  });
  const [evaluationData, setEvaluationData] = useState<EvaluationFormType>({
    title: '',
    content: '',
    score: 3
  });

  const dateFormatter = (date: Date) => {
    const newDate = new Date(date);
    return newDate.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        if (!id) return;
        const res = await getProduct(Number(id), selectedSort);
        const data = res.data.products;
        const sortedImageData = data.image.sort(
          (
            a: {
              id: number;
              name: string;
              path: string;
              sort: number;
            },
            b: {
              id: number;
              name: string;
              path: string;
              sort: number;
            }
          ) => ((a && a.sort) || 0) - ((b && b.sort) || 0)
        );

        setProductData({
          id: data.id,
          alias: data.alias,
          category: `${data.category.name} / ${data.material.name}`,
          name: data.name,
          specification: data.specification,
          calculatedEmissionIntensity: data.calculated_emission_intensity,
          calculatedMethod: data.calculated_method,
          calculatedReduceRate: data.calculated_reduce_rate,
          image: sortedImageData
        });
        setCo2Unit(data.material.co2_unit);
        const operatorEditFlg = operatorFlg.operator;
        const resEval = await getAdminProductEvaluation(
          Number(id),
          operatorEditFlg
        );
        if (resEval.data.total_count === 0) return;
        const dataEval = resEval.data.evaluations;
        if (dataEval === null) return;
        setEvaluationData({
          id: dataEval[0].id,
          score: dataEval[0].score,
          status: dataEval[0].status,
          content: dataEval[0].content,
          nickname: dataEval[0].nickname,
          title: dataEval[0].title,
          updateDate: dateFormatter(dataEval[0].update_date)
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          setErrorText(loginErrorResponse.data.message);
        } else {
          setErrorText(common);
        }
      } finally {
        setSnackbarOpen(true);
        setLoading(false);
      }
    })();
  }, [id, selectedSort]);

  // 削除確認ダイアログ
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  // ローディング
  const [loading, setLoading] = useState<boolean>(false);

  // スナックバー
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [infoText, setInfoText] = useState<string>('');

  const onDelete = async (): Promise<void> => {
    setLoading(true);
    try {
      setLoading(true);
      if (!evaluationData.id) return;
      await adminDeleteReview(evaluationData.id);
      setSnackbarOpen(true);
      setInfoText('削除しました。');
      setEvaluationData({
        content: '',
        nickname: '管理者',
        title: ''
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setSnackbarOpen(true);
      } else {
        setErrorText(common);
        setSnackbarOpen(true);
      }
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  return (
    <>
      <CustomHead title="事務局 評価 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid container>
        <SideBar />
        {/* 右側のコンテンツ */}
        <Grid
          container
          padding={'32px'}
          sx={{
            '@media screen and (max-width:960px)': {
              marginLeft: 0,
              marginTop: spHeaderHeight
            },
            '@media screen and (max-width:600px)': {
              padding: '24px 15px'
            },
            marginLeft: sideBarWidth
          }}
        >
          {/* ヘッダー */}
          <Grid
            container
            justifyContent={'space-between'}
            alignItems={'center'}
            mb={'25px'}
            sx={{
              '@media screen and (max-width:600px)': {
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '25px'
              },
              flexDirection: 'row'
            }}
          >
            <Typography sx={styles.headerTitle}>口コミを書く</Typography>
            <Grid sx={styles.buttonWidth}>
              <CustomSelectField
                list={productMethodList}
                onChange={handleChangeSort}
                value={selectedSort}
                type="primary"
              />
            </Grid>
          </Grid>
          <Divider sx={{ color: gray70, width: '100%' }} />
          {/* コンテンツ */}
          <Grid container sx={styles.contentWrapper}>
            <Grid item sx={styles.imageWrapper}>
              <CardMedia
                component="img"
                image={productData?.image[0]?.path ?? noImage}
              />
            </Grid>
            <Grid item sx={styles.contentDetail}>
              {/* alias category section */}
              <Grid container flexWrap={'nowrap'} gap={'16px'}>
                <Grid item sx={styles.imageForSp}>
                  <CardMedia
                    component="img"
                    image={productData?.image[0]?.path ?? noImage}
                  />
                </Grid>
                <Grid item width={'100%'}>
                  <Grid
                    container
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    justifyContent={'space-between'}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    mb={{ xs: '8px', sm: '4px' }}
                  >
                    <Typography sx={styles.alias}>
                      {productData.alias}
                    </Typography>
                    <Typography sx={styles.category}>
                      {productData.category}
                    </Typography>
                  </Grid>

                  {/* title section */}
                  <Grid mb={'8px'}>
                    <Typography sx={styles.name}>
                      {productData.name}
                      <Typography
                        component={'span'}
                        sx={[styles.subTitle, styles.textOverflow]}
                      >
                        {productData.specification}
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {/* co2 value section */}
              <Grid
                container
                flexDirection={'row'}
                flexWrap={'nowrap'}
                justifyContent={'space-between'}
                alignItems={'flex-end'}
                columnGap={'24px'}
                mb={'12px'}
              >
                <Grid
                  item
                  container
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  columnGap={'4px'}
                  rowGap={'5px'}
                >
                  <Box>
                    <Typography component="span" sx={styles.co2ValueItem}>
                      CO2原単位
                    </Typography>
                    {productData.calculatedMethod === 1 ? (
                      <Chip label="換算値" sx={[styles.convValueChip]} />
                    ) : productData.calculatedMethod === 2 ? (
                      <Chip label="算定値" sx={[styles.calcValueChip]} />
                    ) : (
                      <></>
                    )}
                    <Typography component="span" sx={styles.co2Value}>
                      {productData.calculatedEmissionIntensity?.toLocaleString() ??
                        'ー'}
                    </Typography>
                    <Typography
                      component="span"
                      sx={[styles.co2ValueUnit, { mr: '24px' }]}
                    >
                      kg-CO2/{co2Unit}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography component="span" sx={styles.co2ValueItem}>
                      CO2削減率
                    </Typography>
                    <ChangeHistoryOutlinedIcon sx={styles.co2ValueIcon} />
                    <Typography component="span" sx={styles.co2Value}>
                      {productData.calculatedReduceRate ?? 'ー'}
                    </Typography>
                    <Typography component="span" sx={styles.co2ValueUnit}>
                      %
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <ReviewInputBox
                evaluation={evaluationData}
                adminFlg
                productId={Number(id)}
                setEvaluationData={setEvaluationData}
              />

              <Grid container justifyContent="flex-end">
                <Button
                  onClick={() => setDialogOpen(true)}
                  startIcon={<DeleteOutlined sx={{ color: gray90 }} />}
                  sx={[tertiaryButtonOutlinedColor, styles.deleteButton]}
                  variant="outlined"
                >
                  評価を削除する
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <CustomDeleteDialog
        setDialogOpen={setDialogOpen}
        onDelete={onDelete}
        open={dialogOpen}
        title="評価を削除"
      />
      <CustomSnackbar
        autoHideDuration={6000}
        infoText={infoText}
        onClose={() => setSnackbarOpen(false)}
        open={snackbarOpen}
        text={errorText}
      />
    </>
  );
};

export default AdminEvaluation;
